import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { hasValue } from '../utils/content';

const imageDef: SlotGroupDefinition = {
  label: 'Banner Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

interface PageHeroProps {
  tokenPrefix?: string;
  title?: string;
  subtitle?: string;
  /** Property field used as the default intro copy (e.g. property_floorplans_blurb). */
  blurbField?: string;
  /** photo_gallery index used as the default banner photo. */
  imageIndex?: number;
}

/** Interior page opener: photo band, then a centered title, subtitle, and page blurb. */
export function PageHero({ tokenPrefix, title, subtitle, blurbField, imageIndex }: PageHeroProps) {
  const prefix = tokenPrefix || 'pagehero';
  const titlePath = `${prefix}.title`;
  const subtitlePath = `${prefix}.subtitle`;
  const blurbPath = `${prefix}.blurb`;
  const imagePath = `${prefix}.image`;

  const runtimeImageDef = useMemo<SlotGroupDefinition>(() => {
    if (imageIndex == null || imageIndex === 0) return imageDef;
    return {
      ...imageDef,
      fields: {
        image: { ...imageDef.fields.image, defaultField: `photo_gallery[${imageIndex}].image` },
        alt: { ...imageDef.fields.alt, defaultField: `photo_gallery[${imageIndex}].alt_text` },
      },
      sourceBinding: { ...imageDef.sourceBinding!, defaultIndex: imageIndex },
    };
  }, [imageIndex]);

  const titleValue = useContentToken(titlePath, { defaultValue: title || 'Welcome' });
  const subtitleValue = useContentToken(subtitlePath, { defaultValue: subtitle || '' });
  const image = useSlotGroup(imagePath, runtimeImageDef);

  return (
    <>
      <section className="relative h-[220px] overflow-hidden bg-secondary sm:h-[280px] md:h-[340px] lg:h-[360px]">
        <EditableSlotGroup groupPath={imagePath} definition={runtimeImageDef} className="js-parallax absolute inset-0">
          <VibesitesImage
            url={image.image || ''}
            alt={hasValue(image.alt) ? image.alt : ''}
            priority
            sizes="100vw"
            className="js-kenburns h-full w-full object-cover"
          />
        </EditableSlotGroup>
      </section>

      <section className="bg-background pb-6 pt-10 md:pb-8 md:pt-14">
        <div className="mx-auto max-w-[920px] px-6 text-center">
          <EditableToken path={titlePath} defaultValue={title || 'Welcome'} as="h1" className="js-enter js-d1 js-heading text-[32px] text-foreground sm:text-[40px] lg:text-[45px]">
            {titleValue || title}
          </EditableToken>
          {(hasValue(subtitleValue) || hasValue(subtitle)) && (
            <EditableToken
              path={subtitlePath}
              defaultValue={subtitle || ''}
              as="h2"
              className="js-enter js-d2 js-heading mt-3 text-[20px] !font-semibold text-foreground sm:text-[24px]"
            >
              {subtitleValue || subtitle}
            </EditableToken>
          )}
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            as="div"
            className="js-enter js-d3 js-copy mx-auto mt-5 max-w-[880px] text-foreground"
          />
        </div>
      </section>

      <EditableToken path="pagehero.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="pagehero.subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="pagehero.blurb" as="div" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="pagehero.image" definition={imageDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageHero',
  displayName: 'Page Hero',
  tokens: {
    'pagehero.title': { label: 'Title', type: 'text', defaultValue: 'Welcome' },
    'pagehero.subtitle': { label: 'Subtitle', type: 'text', defaultValue: '' },
    'pagehero.blurb': {
      label: 'Intro Copy',
      type: 'richtext',
      defaultValue: '',
      description: 'Defaults to the page blurb field set on this page section.',
    },
  },
  slotGroups: {
    'pagehero.image': imageDef,
  },
};

(PageHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageHero;
